import React, { useState, useRef, useMemo } from 'react';
import { LoadingSpinner } from 'yankees-react-utils';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import useOpposingTeamSelector from '../../../../utils/useOpposingTeamSelector';
import useConfig from '../../../../utils/useConfig';
import usePlayerSelector from '../../../../utils/usePlayerSelector';
import { Advance, Player, Team, isLhb as isLhbFunc, useOktaQuery, useCachedOktaQuery } from '../../../../data';
import PitcherHeader from '../PitcherHeader';
import Filters from './Filters';
import PlayerAuthoredReportNote from '../../../../components/Notes/PlayerAuthoredReportNote';
import OpposingPitcherKeyTakeawaysNote from '../OpposingPitcherKeyTakeawaysNote';
import PitchUsage from './PitchUsage';
import PitchLocation from './PitchLocation';
import PitchLocationsByCount from './PitchLocationsByCount';
import VideoPlayerModal from './VideoPlayerModal';

const StyledContainer = styled.div`
    width: 95%;
    margin: 12px auto;

    .card-body h4 {
        text-transform: uppercase;
    }

    .header-wrapper {
        .header-divider {
            display: none;
        }
        @media (min-width: 1195px) {
            display: flex;
            .header-divider {
                display: inline;
            }
        }
    }
`;

const GamePrep = ({ pitcherId }) => {
    const { t: translate } = useTranslation();
    const { UsePreviousYearData } = useConfig();

    const batter = usePlayerSelector();
    const opposingTeam = useOpposingTeamSelector();

    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear() - (UsePreviousYearData ? 1 : 0), 0, 1));
    const [endDate, setEndDate] = useState(new Date());
    const [catcher, setCatcher] = useState({ name: translate('common.all'), value: null });
    const [vsTeam, setVsTeam] = useState({ name: translate('common.all'), value: null });
    const [lastApps, setLastApps] = useState({ name: translate('common.all'), value: null });
    const [playlistPitches, setPlaylistPitches] = useState([]);

    const videoPlayerModalContainerRef = useRef(null);

    const { data: pitcher, isFetching: isLoadingPitcher } = useCachedOktaQuery(
        ['player', pitcherId],
        (t) => Player.get(t, { playerId: pitcherId }),
    );
    const { data: teams, isLoading: isLoadingTeams } = useCachedOktaQuery(
        ['teams'],
        (t) => Team.bylevel(t, opposingTeam.LevelID),
        { enabled: !!opposingTeam },
    );

    const isLhb = isLhbFunc(pitcher, batter);
    const levelId = opposingTeam.LevelID !== 8 ? 7 : 8;

    const pitcherOverviewParams = {
        pitcherId,
        levelId,
        isLhb,
        vsTeamId: vsTeam.value,
        catcherId: catcher.value,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        lastApps: lastApps.value,
    };
    const { data: pitcherOverview, isLoading: isLoadingPitcherOverview } = useOktaQuery(
        ['pitcherOverview', pitcherOverviewParams],
        (t) => Advance.pitcherOverview(t, pitcherOverviewParams),
        { enabled: !!pitcher },
    );

    const { splits, catchers, repertoire, lgavgkzon, pitches } = pitcherOverview || {};
    const cleanPitches = useMemo(() => pitches?.map((p) => ({ ...p, platex: p.platex * -1 })).filter((p) => p.fxtype !== 1) ?? [], [pitches]);

    if (isLoadingPitcher) {
        return <div className="py-5 text-center"> <LoadingSpinner /> </div>;
    }

    if (isLoadingPitcherOverview || isLoadingTeams) {
        return (
            <StyledContainer>
                <Row>
                    <Col sm={12}>
                        <PitcherHeader pitcher={pitcher} isLhb={isLhb} levelId={opposingTeam.LevelID} />
                    </Col>
                </Row>
                <div className="py-5 text-center"> <LoadingSpinner /> </div>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer ref={videoPlayerModalContainerRef}>
            <Row className="align-items-center">
                <Col md={5} sm={12}>
                    <PitcherHeader pitcher={pitcher} isLhb={isLhb} splits={splits} levelId={opposingTeam.LevelID} />
                </Col>
                <Col md={7} sm={12}>
                    <Filters
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        catchers={catchers}
                        catcher={catcher}
                        setCatcher={setCatcher}
                        teams={teams}
                        vsTeam={vsTeam}
                        setVsTeam={setVsTeam}
                        lastApps={lastApps}
                        setLastApps={setLastApps}
                    />
                </Col>
            </Row>
            <Row className="mt-3">
                <Col md={6} sm={12}>
                    <OpposingPitcherKeyTakeawaysNote
                        title={translate('sections.opponent.player.attack-plan')}
                        pitcherId={pitcherId}
                        islhb={isLhb}
                    />
                </Col>
                <Col md={6} sm={12}>
                    <PlayerAuthoredReportNote type="mynt" section="1" player={batter} pitcher={pitcher} />
                </Col>
            </Row>
            <Row className="mt-3">
                <Col sm={12}>
                    <PitchUsage isLhb={isLhb} repertoire={repertoire} lgavgkzon={lgavgkzon} />
                </Col>
            </Row>
            <Row className="mt-3">
                <Col sm={12}>
                    <PitchLocation
                        playAll={setPlaylistPitches}
                        isLhb={isLhb}
                        batter={batter}
                        pitches={cleanPitches}
                        catchers={catchers}
                        teams={teams}
                        pitcher={pitcher}
                        modalContainer={videoPlayerModalContainerRef}
                    />
                </Col>
            </Row>
            <Row className="mt-3">
                <Col sm={12}>
                    <PitchLocationsByCount
                        isLhb={isLhb}
                        pitcher={pitcher}
                        repertoire={repertoire}
                        pitches={cleanPitches}
                        startDate={startDate}
                        playAll={setPlaylistPitches}
                    />
                </Col>
            </Row>
            <VideoPlayerModal
                container={videoPlayerModalContainerRef}
                playlistPitches={playlistPitches}
            />
        </StyledContainer>
    );
};

export default GamePrep;
